import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { operatorApi, type Tenant, type EdgeNode, type ScadaDevice } from '../../api/operator'
import { useAdminToken } from './AdminLayout'
import { Badge } from '../../components/Badge'

const inputCls = 'w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden'

// ── SCADA thresholds section ──────────────────────────────────────────────────

function ScadaThresholdsSection({ tenant }: { tenant: Tenant }) {
  const token = useAdminToken()

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['op-scada-devices', tenant.id, token],
    queryFn:  () => operatorApi.listScadaDevices(token, tenant.id).then((r) => r.data),
    enabled:  !!tenant.id && !!token,
    staleTime: 30_000,
  })

  if (!tenant.modules?.includes('scada')) return null

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900">SCADA — pragovi upozorenja</h2>
        <p className="text-xs text-gray-500 mt-0.5">Nema signala: prazno = podrazumevano (15 min). Pumpa stoji: prazno = upozorenje isključeno.</p>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
        </div>
      ) : devices.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-500">Nema SCADA uređaja.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Uređaj', 'Tip', 'Prag (nema signala — min)', 'Prag (pumpa stoji — min)', 'Sačuvati'].map((h) => (
                <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <ScadaThresholdRow key={d.device_key} tenant={tenant} device={d} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function ScadaThresholdRow({ tenant, device }: { tenant: Tenant; device: ScadaDevice }) {
  const token = useAdminToken()
  const qc    = useQueryClient()
  const [offlineMin, setOfflineMin] = useState<string>(device.alert_offline_minutes?.toString() ?? '')
  const [stoppedMin, setStoppedMin] = useState<string>(device.alert_stopped_minutes?.toString() ?? '')
  const [saved, setSaved] = useState(false)

  const mut = useMutation({
    mutationFn: () => {
      const body: { alert_offline_minutes?: number | null; alert_stopped_minutes?: number | null } = {
        alert_offline_minutes: offlineMin !== '' ? parseInt(offlineMin, 10) : null,
      }
      if (device.node_type === 'pump') {
        body.alert_stopped_minutes = stoppedMin !== '' ? parseInt(stoppedMin, 10) : null
      }
      return operatorApi.setDeviceThresholds(token, tenant.id, device.device_key, body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['op-scada-devices', tenant.id, token] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
  })

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50">
      <td className="px-5 py-3">
        <p className="text-gray-900 font-medium">{device.label}</p>
        <p className="text-xs text-gray-500">{device.device_key}</p>
      </td>
      <td className="px-5 py-3 text-xs text-gray-500">
        {device.node_type === 'pump' ? 'Pumpa' : 'Rezervoar'}
      </td>
      <td className="px-5 py-3">
        <input
          type="number" min="1" placeholder="npr. 15"
          value={offlineMin}
          onChange={(e) => setOfflineMin(e.target.value)}
          className="w-28 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 outline-hidden"
        />
      </td>
      <td className="px-5 py-3">
        {device.node_type === 'pump' ? (
          <input
            type="number" min="1" placeholder="npr. 10"
            value={stoppedMin}
            onChange={(e) => setStoppedMin(e.target.value)}
            className="w-28 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 outline-hidden"
          />
        ) : <span className="text-gray-400">—</span>}
      </td>
      <td className="px-5 py-3">
        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
            saved
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-gray-100 text-gray-700 hover:text-gray-900 hover:bg-gray-200'
          }`}
        >
          {saved ? '✓ Sačuvano' : 'Sačuvati'}
        </button>
      </td>
    </tr>
  )
}


// ── Edge Nodes section ────────────────────────────────────────────────────────

function EdgeNodesSection({ tenant }: { tenant: Tenant }) {
  const token = useAdminToken()
  const qc    = useQueryClient()

  const { data: nodes = [], isLoading } = useQuery({
    queryKey: ['op-edge-nodes', tenant.id, token],
    queryFn: () => operatorApi.listEdgeNodes(token, tenant.id).then((r) => r.data),
    staleTime: 30_000,
  })

  const [newName, setNewName]           = useState('')
  const [addErr, setAddErr]             = useState('')
  const [revealId, setRevealId]         = useState<string | null>(null)
  const [confirmRegen, setConfirmRegen] = useState<EdgeNode | null>(null)
  const [confirmDel, setConfirmDel]     = useState<EdgeNode | null>(null)
  const [regenErr, setRegenErr]         = useState('')

  const [renamingId, setRenamingId]     = useState<string | null>(null)
  const [renameVal, setRenameVal]       = useState('')

  const invalidate = () => qc.invalidateQueries({ queryKey: ['op-edge-nodes', tenant.id, token] })

  const createMutation = useMutation({
    mutationFn: () => operatorApi.createEdgeNode(token, tenant.id, newName.trim()),
    onSuccess: () => { setNewName(''); setAddErr(''); invalidate() },
    onError: (e: unknown) =>
      setAddErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška.'),
  })

  const regenMutation = useMutation({
    mutationFn: (nodeId: string) => operatorApi.regenerateEdgeKey(token, tenant.id, nodeId),
    onSuccess: () => { setConfirmRegen(null); setRegenErr(''); invalidate() },
    onError: (e: unknown) =>
      setRegenErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška.'),
  })

  const deactivateMutation = useMutation({
    mutationFn: (node: EdgeNode) =>
      operatorApi.updateEdgeNode(token, tenant.id, node.id, { active: !node.active }),
    onSuccess: () => invalidate(),
  })

  const deleteMutation = useMutation({
    mutationFn: (nodeId: string) => operatorApi.deleteEdgeNode(token, tenant.id, nodeId),
    onSuccess: () => { setConfirmDel(null); invalidate() },
  })

  const renameMutation = useMutation({
    mutationFn: (node: EdgeNode) =>
      operatorApi.updateEdgeNode(token, tenant.id, node.id, { name: renameVal.trim() }),
    onSuccess: () => { setRenamingId(null); setRenameVal(''); invalidate() },
  })

  const handleDownloadPackage = (node: EdgeNode) => {
    // Direct link — browser downloads the 50+ MB ZIP natively without
    // buffering it into JS memory (fetch+blob would truncate large files)
    const url = operatorApi.edgePackageUrl(tenant.id, node.id, token)
    const a = document.createElement('a')
    a.href = url
    a.download = ''
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const maskKey = (key: string) => '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••' + key.slice(-4)

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900">Edge čvorovi</h2>
        <p className="text-xs text-gray-500 mt-0.5">Svako čvor ima jedinstven ključ za slanje podataka sa terena.</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
        </div>
      ) : nodes.length === 0 ? (
        <div className="px-6 py-6 text-sm text-gray-500">Nema edge čvorovi. Dodajte prvo ispod.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Naziv', 'Ključ', 'Poslednji kontakt', 'Status', 'Akcije'].map((h) => (
                <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nodes.map((node) => (
              <tr key={node.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-5 py-3">
                  {renamingId === node.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        autoFocus
                        value={renameVal}
                        onChange={(e) => setRenameVal(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && renameVal.trim()) renameMutation.mutate(node)
                          if (e.key === 'Escape') { setRenamingId(null); setRenameVal('') }
                        }}
                        className="border border-gray-300 rounded-sm px-2 py-1 text-sm text-gray-900 w-36 focus:outline-hidden focus:border-blue-500"
                      />
                      <button
                        onClick={() => renameVal.trim() && renameMutation.mutate(node)}
                        disabled={!renameVal.trim() || renameMutation.isPending}
                        className="text-emerald-600 hover:text-emerald-700 disabled:opacity-40 px-1"
                        title="Sačuvati"
                      >✓</button>
                      <button
                        onClick={() => { setRenamingId(null); setRenameVal('') }}
                        className="text-gray-400 hover:text-gray-700 px-1"
                        title="Otkazati"
                      >✕</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 group">
                      <span className="text-gray-900 font-medium">{node.name}</span>
                      <button
                        onClick={() => { setRenamingId(node.id); setRenameVal(node.name) }}
                        className="text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Preименовати"
                      >✎</button>
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 max-w-[160px]">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-500 truncate">
                      {revealId === node.id ? node.ingress_key : maskKey(node.ingress_key)}
                    </span>
                    <button
                      onClick={() => setRevealId(revealId === node.id ? null : node.id)}
                      className="text-xs text-gray-400 hover:text-gray-700 transition-colors shrink-0"
                      title={revealId === node.id ? 'Sakriti' : 'Prikazati ključ'}
                    >
                      {revealId === node.id ? '◉' : '○'}
                    </button>
                  </div>
                </td>
                <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">
                  {node.last_seen_at ? (
                    <span title={node.last_seen_ip ?? undefined}>
                      {new Date(node.last_seen_at).toLocaleString('sr-Latn-RS')}
                    </span>
                  ) : (
                    <span className="text-gray-400">Nikad</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  <Badge tone={node.active ? 'success' : 'critical'}>
                    {node.active ? 'Aktivan' : 'Neaktivan'}
                  </Badge>
                </td>
                <td className="px-5 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDownloadPackage(node)}
                      className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight hover:bg-blue-50 px-2 py-1.5 rounded-lg transition-colors"
                    >
                      Preuzmi
                    </button>
                    <button
                      onClick={() => { setRegenErr(''); setConfirmRegen(node) }}
                      className="text-xs font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 px-2 py-1.5 rounded-lg transition-colors"
                    >
                      Novi ključ
                    </button>
                    <button
                      onClick={() => deactivateMutation.mutate(node)}
                      className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 py-1.5 rounded-lg transition-colors"
                    >
                      {node.active ? 'Deaktivirati' : 'Aktivirati'}
                    </button>
                    <button
                      onClick={() => setConfirmDel(node)}
                      className="text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1.5 rounded-lg transition-colors"
                    >
                      Obrisati
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add new node */}
      <div className="px-6 py-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && newName.trim() && createMutation.mutate()}
            placeholder="Naziv čvorovi (npr. SCADA Stanica 1)"
            className={inputCls + ' flex-1'}
          />
          <button
            onClick={() => createMutation.mutate()}
            disabled={!newName.trim() || createMutation.isPending}
            className="shrink-0 bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 text-brand-midnight text-sm font-medium px-4 py-2 rounded-lg"
          >
            {createMutation.isPending ? 'Dodavanje…' : 'Dodati'}
          </button>
        </div>
        {addErr && <p className="mt-2 text-xs text-red-600">{addErr}</p>}
      </div>

      {/* Regenerate key confirmation modal */}
      {confirmRegen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setConfirmRegen(null)} />
          <div className="relative bg-white border border-amber-200 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Generisati novi ključ?</h2>
            <p className="text-sm text-gray-500">
              Čvorište <span className="text-gray-900 font-semibold">{confirmRegen.name}</span> — stari ključ odmah postaje nevažeći.
              Edge server mora biti ažuriran sa novim <code className="text-xs bg-gray-100 px-1 rounded-sm">config.yaml</code>.
            </p>
            {regenErr && <p className="text-xs text-red-600">{regenErr}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => regenMutation.mutate(confirmRegen.id)}
                disabled={regenMutation.isPending}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50"
              >
                {regenMutation.isPending ? 'Generisanje…' : 'Generisati novi ključ'}
              </button>
              <button onClick={() => setConfirmRegen(null)}
                className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
                Otkazati
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setConfirmDel(null)} />
          <div className="relative bg-white border border-red-200 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Obrisati čvor?</h2>
            <p className="text-sm text-gray-500">
              <span className="text-gray-900 font-semibold">{confirmDel.name}</span> — ključ postaje nevažeći odmah.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => deleteMutation.mutate(confirmDel.id)}
                disabled={deleteMutation.isPending}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Brisanje…' : 'Obrisati'}
              </button>
              <button onClick={() => setConfirmDel(null)}
                className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
                Otkazati
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


export default function AdminTenantScada({ tenant }: { tenant: Tenant }) {
  return (
    <>
      <ScadaThresholdsSection tenant={tenant} />
      <EdgeNodesSection tenant={tenant} />
    </>
  )
}
